import { useEffect, useState } from "react";
import { NavLink, Link, useLocation } from "react-router-dom";
import { Menu, ArrowRight } from "lucide-react";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { NAV } from "@/content/site";
import { Logo } from "./Logo";

const linkCls = ({ isActive }) =>
  `relative px-1 py-2 text-[14px] font-medium transition-colors duration-200 ${
    isActive ? "text-plum after:absolute after:inset-x-1 after:-bottom-0.5 after:h-[2px] after:rounded-full after:bg-teal" : "text-slate hover:text-plum"
  }`;

export const Navbar = () => {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const { pathname } = useLocation();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => setOpen(false), [pathname]);

  return (
    <header
      data-testid="site-header"
      className={`sticky top-0 z-40 border-b backdrop-blur-xl transition-colors duration-300 ${
        scrolled ? "border-plum/10 bg-white/85" : "border-transparent bg-white/60"
      }`}
    >
      <nav aria-label="Main" className="container-x flex h-[72px] items-center justify-between gap-6">
        <Logo />
        <ul className="hidden items-center gap-6 xl:flex">
          {NAV.slice(1).map((item) => (
            <li key={item.to}>
              <NavLink to={item.to} className={linkCls} data-testid={`nav-link-${item.to.slice(1)}`}>
                {item.label}
              </NavLink>
            </li>
          ))}
        </ul>
        <div className="flex items-center gap-3">
          <Link
            to="/partners#enquiry"
            data-testid="nav-cta-partner"
            className="hidden items-center gap-2 rounded-full bg-teal px-5 py-2.5 text-sm font-semibold text-white transition-[background-color,transform] duration-200 hover:bg-teal-700 active:scale-95 sm:inline-flex"
          >
            Partner With Us <ArrowRight className="h-4 w-4" />
          </Link>
          <Sheet open={open} onOpenChange={setOpen}>
            <SheetTrigger
              data-testid="mobile-menu-button"
              aria-label="Open menu"
              className="inline-flex h-11 w-11 items-center justify-center rounded-full border border-plum/15 text-plum transition-colors hover:bg-mint xl:hidden"
            >
              <Menu className="h-5 w-5" />
            </SheetTrigger>
            <SheetContent side="right" className="flex w-[86%] flex-col bg-white p-0 sm:max-w-sm" data-testid="mobile-menu">
              <div className="border-b border-plum/10 px-6 py-5">
                <SheetTitle className="sr-only">Site navigation</SheetTitle>
                <SheetDescription className="sr-only">Pages of the SwastSaathi website</SheetDescription>
                <Logo testId="mobile-menu-logo" onClick={() => setOpen(false)} />
              </div>
              <ul className="flex-1 space-y-1 overflow-y-auto px-4 py-5">
                {NAV.map((item) => (
                  <li key={item.to}>
                    <NavLink
                      to={item.to}
                      end
                      onClick={() => setOpen(false)}
                      data-testid={`mobile-nav-link-${item.to === "/" ? "home" : item.to.slice(1)}`}
                      className={({ isActive }) =>
                        `flex items-center justify-between rounded-xl px-4 py-3.5 font-display text-lg transition-colors ${
                          isActive ? "bg-mint text-plum" : "text-ink hover:bg-mint/60"
                        }`
                      }
                    >
                      {item.label}
                      <ArrowRight className="h-4 w-4 opacity-40" />
                    </NavLink>
                  </li>
                ))}
              </ul>
              <div className="border-t border-plum/10 p-5">
                <Link
                  to="/partners#enquiry"
                  onClick={() => setOpen(false)}
                  data-testid="mobile-nav-cta-partner"
                  className="flex w-full items-center justify-center gap-2 rounded-full bg-teal px-5 py-3.5 font-semibold text-white hover:bg-teal-700"
                >
                  Partner With Us <ArrowRight className="h-4 w-4" />
                </Link>
              </div>
            </SheetContent>
          </Sheet>
        </div>
      </nav>
    </header>
  );
};
